































import { defineComponent, ref, computed, provide, watch, PropType } from "@vue/composition-api";
import { debounce } from "lodash-es";
import { ContentSet, InputContentSetUpdate } from "@scrile/api-provider/dist/api/ContentSetProvider";
import { PaymentPackage } from "@scrile/api-provider/dist/api/PaymentPackageProvider";
import { SubscriptionLevel } from "@scrile/api-provider/dist/api/SubscriptionLevelProvider";
import { ContentFeedTypeEnum } from "@scrile/api-provider/dist/projects/scrileConnect/ContentFeedProvider";
import { $t } from "@/lib/i18nHelpers";
import { PostFileData } from "@/types";
import BaseCheckbox from "@/components/BaseCheckbox";
import CustomScroll from "@/components/CustomScroll";
import PageTitle from "@/components/PageTitle";
import EditPostOptions from "../EditPostOptions";
import EventPostEditView from "../EventPostEditView";
import InputWithMentions from "../InputWithMetions";
import MediaPostEditView from "../MediaPostEditView";
import PostPublishConfirm from "../PostPublishConfirm";
import SafeForWorkOption from "../SafeForWorkOption";

export default defineComponent({
  name: "EditPostOverview",
  components: {
    PostPublishConfirm,
    SafeForWorkOption,
    BaseCheckbox,
    EditPostOptions,
    PageTitle,
    InputWithMentions,
    CustomScroll,
  },
  props: {
    postData: { type: Object as PropType<ContentSet>, required: true },
    fileList: { type: Array as PropType<PostFileData[]>, required: true },
  },
  setup(props, { emit }) {
    const description = ref(props.postData.description ?? "");
    const updateCb = ref<(() => Promise<void>)[]>([]);
    const recurringLevel = ref<SubscriptionLevel | null>(null);
    const oneTimePrice = ref<PaymentPackage | null>(null);
    const showPublishConfirm = ref(false);
    const publishCB = ref<Function>(() => {});

    const type = computed(() => {
      if (!props.postData) return "";
      return props.postData.type;
    });

    const uploading = computed(() => props.fileList.some(f => f.uploading));

    const component = computed(() => {
      if (type.value === ContentFeedTypeEnum.EVENT) return EventPostEditView;
      return MediaPostEditView;
    });

    const title = computed(() => {
      return $t("{0} {1}", [
        !props.postData.published ? $t("New") : $t("Edit"),
        props.postData.type === ContentFeedTypeEnum.MEDIA ? $t("post") : $t("event"),
      ]);
    });

    const onUpdatePostPrice = ({
      recurringLevel: updatedRecurringLevel,
      oneTimePackage,
    }: {
      recurringLevel: SubscriptionLevel | null;
      oneTimePackage: PaymentPackage | null;
    }) => {
      recurringLevel.value = updatedRecurringLevel;
      oneTimePrice.value = oneTimePackage;
    };

    const onPublish = (cb: Function) => {
      const data: Omit<InputContentSetUpdate, "id"> = {
        description: description.value,
        published: true,
      };
      const update = () => Promise.all(updateCb.value.map(i => i()));
      emit("update", { data, closeOnSave: true, beforeUpdate: update }, cb);
    };

    const handlePublish = (cb: Function) => {
      const isPaidPost =
        props.postData.type !== ContentFeedTypeEnum.EVENT &&
        (recurringLevel.value?.pricePackage?.amount ?? oneTimePrice.value?.amount ?? 0) > 0;
      const isFreeFileInPost = props.fileList.some(file => file.public);
      if (isPaidPost && isFreeFileInPost) {
        publishCB.value = cb;
        showPublishConfirm.value = true;
      } else {
        onPublish(cb);
      }
    };
    const onConfirmedPublish = () => {
      onPublish(publishCB.value);
    };
    const onDeclinePublish = () => {
      publishCB.value();
      showPublishConfirm.value = false;
    };

    const onSetUpdateCb = (cb: () => Promise<void>) => {
      updateCb.value.push(cb);
      emit("onSetUpdateCb", cb);
    };

    const removeCb = (cb: () => Promise<void>) => {
      updateCb.value = updateCb.value.filter(i => i !== cb);
      emit("onSetUpdateCb", cb);
    };

    watch(
      description,
      debounce((description: string) => {
        const data: Omit<InputContentSetUpdate, "id"> = {
          description,
        };
        emit("update", { data });
      })
    );

    provide("setUpdateCb", onSetUpdateCb);
    provide("removeCb", removeCb);

    return {
      description,
      type,
      uploading,
      component,
      title,
      showPublishConfirm,
      onPublish,
      onSetUpdateCb,
      onUpdatePostPrice,
      handlePublish,
      onConfirmedPublish,
      onDeclinePublish,
    };
  },
});
